import React, { useState } from "react";
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setCompanyId } from "../utils/companyStorage";

export default function LoginPage() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // /super から来た場合でも selectedTenant を上書きしない（ecxテナントもGoogleログイン可にする）

    // LoginPage.tsx（追加）安全なリダイレクト判定
    function isSafeRedirect(p: string) {
        // オープンリダイレクト対策：必ず相対パスのみ許可
        return typeof p === "string" && p.startsWith("/") && !p.startsWith("//") && !p.startsWith("/login");
    }

    const acceptInviteIfPresent = async () => {
        const tenantId = searchParams.get("tenantId");
        const accountId = searchParams.get("accountId");
        const inviteId = searchParams.get("inviteId");
        if (!tenantId || !accountId || !inviteId) return;

        localStorage.setItem("selectedTenant", tenantId);
        sessionStorage.setItem("login_tenant", tenantId);
        setCompanyId(accountId);

        const auth = getAuth();
        const token = await auth.currentUser?.getIdToken();
        if (!token) return;

        const resp = await fetch("/api/admin/invites/accept", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ tenantId, accountId, inviteId }),
        });

        if (!resp.ok) {
            let msg = "招待の確定に失敗しました。";
            try {
                const data = await resp.json();
                msg = data?.error || msg;
            } catch { }
            throw new Error(msg);
        }
    };


    const login = async () => {
        const auth = getAuth();
        const provider = new GoogleAuthProvider();

        setLoading(true);
        setError(null);

        try {
            await signInWithPopup(auth, provider);

            // /super などで事前に選ばれたテナントがある場合は上書きしない
            const preSelectedTenant = String(localStorage.getItem("selectedTenant") || "").trim();
            if (!preSelectedTenant) {
                localStorage.setItem("selectedTenant", "ecvision");
            }

            await acceptInviteIfPresent();

            // ✅ 追加：query next を優先（任意）
            const nextParam = searchParams.get("next");
            const redirectRaw =
                (nextParam ? decodeURIComponent(nextParam) : "") ||
                localStorage.getItem("postLoginRedirect") ||
                "";

            const redirect = isSafeRedirect(redirectRaw) ? redirectRaw : "";

            if (redirect.startsWith("/super")) {
                localStorage.removeItem("postLoginRedirect");
                sessionStorage.removeItem("force_firebase_login"); // ✅ 追加：後腐れ防止
                window.location.href = redirect;
                return;
            }

            navigate("/check-status", { replace: true });
        } catch (e: any) {
            console.error("ログインエラー:", e);
            const errorMsg = e?.message || "ログインに失敗しました。時間をおいて再度お試しください。";
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-100">
            <div className="w-full max-w-md px-4">
                <div className="mb-6 text-center">
                    <div className="flex items-center justify-center mt-4">
                        <img src="/logo_landscape.webp" alt="ec vision" className="h-6 rounded-xl object-cover" />
                    </div>

                    <div className="flex items-center justify-center mt-4">
                        <img src="/logo_navi.png" alt="利益Navi" className="h-16 rounded-xl object-contain" />
                    </div>

                    <p className="mt-2 text-xs text-slate-500">Google アカウントでサインインしてください。</p>
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                    {error && (
                        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                            {error}
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={login}
                        disabled={loading}
                        className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-80"
                    >
                        <span>{loading ? "ログイン中..." : "Googleでログイン"}</span>
                    </button>

                    <p className="mt-4 text-[11px] text-slate-400">
                        ※ 社内で付与された Google アカウントでログインしてください。
                    </p>
                </div>

                <div className="mt-4 rounded-[10px] border border-[#AFA9EC] border-l-4 border-l-[#534AB7] bg-white p-[1.125rem]">
                    <div className="flex items-start gap-3">
                        <div className="mt-[1px] flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#534AB7]">
                            <svg width="16" height="16" viewBox="0 0 14 14" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                                <circle cx="7" cy="7" r="5" />
                                <line x1="7" y1="4.5" x2="7" y2="7" />
                                <circle cx="7" cy="9.5" r=".4" fill="#fff" />
                            </svg>
                        </div>

                        <div className="flex-1">
                            <div className="mb-1.5 text-sm font-bold text-[#26215C]">初めてログインする方へ 初期設定ガイドをご確認ください</div>
                            <div className="mb-3 text-[13px] leading-7 text-[#5F5E5A]">
                                ログイン後、初期設定を完了することで利益Naviをご利用いただけます。スタンダードプランの初期設定ガイドをご確認ください。
                            </div>
                            <a
                                href="https://ec-vision.co.jp/rieki-navi/setup/standard/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 rounded-[7px] bg-[#534AB7] px-[1.125rem] py-2 text-[13px] font-bold text-white transition hover:opacity-85"
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                                    <path d="M2 3h10v8H2z" />
                                    <polyline points="2 3 7 8 12 3" />
                                </svg>
                                初期設定ガイドを開く ↗
                            </a>
                        </div>
                    </div>
                </div>

                <p className="mt-6 text-[11px] text-center text-slate-400">
                    © {new Date().getFullYear()} ec vision
                </p>
            </div>
        </div>
    );
}
